import React, {
  useContext,
  useState,
  useEffect,
  createRef,
  EventHandler
} from 'react';
import { createPortal } from 'react-dom';
import './Tags.scss';
import intl from 'react-intl-universal';
import classNames from 'classnames';
import { IOneTag, ITags } from './Tags.d';
import { ThemeContext } from '../../store/ThemeProvider';
import chroma from 'chroma-js';
import usePortalDom from '../hooks/usePortalDom';
import {PageContext} from '../ComponentNavigatorV2/PageContext';

const OneShowMoreTag = (props: IOneTag) => {
  const { mainColor, h1Color } = useContext(ThemeContext);
  return (
    <div
      className={`${classNames('one-show-more-type', {
        active: props.isActive
      })}`}
      style={{
        color: props.isActive
          ? h1Color
          : chroma(h1Color)
              .alpha(0.6)
              .css()
      }}
      onClick={() => props.clickTag(props.id)}
    >
      {props.name}

      <div className="border">
        <span style={{ backgroundColor: mainColor }} />
      </div>
    </div>
  );
};

const OneTag = (props: IOneTag) => {
  const { mainColor, h1Color } = useContext(ThemeContext);

  return (
    <div
      className={`${classNames('one-type', {
        active: props.isActive,
        sp: props.sp
      })}`}
      style={{
        color: props.isActive
          ? h1Color
          : chroma(h1Color)
              .alpha(0.6)
              .css()
      }}
      onClick={() => props.clickTag(props.id)}
    >
      {props.name}

      <div className="border">
        <span style={{ backgroundColor: mainColor }} />
      </div>
    </div>
  );
};

let lastTagTypeScrollLeft = 0;

const Tags = React.forwardRef<any, ITags>((props, ref) => {
  const {showall, showperson, showhot} = props;
  // console.log(showall, showperson, showhot);
  const defaultTags = [
    showall !== false ? {
      name: intl.get('all'),
      id: 0
    } : null,
    !!+window.configDetail.use_face && !props.disableSystem && showperson !== false
      ? {
          name: intl.get('Person'),
          id: -1
        }
      : null,
    !props.disableSystem && showhot !== false
      ? {
          name: intl.get('Hot'),
          id: -2
        }
      : null
  ].filter(Boolean) as IOneTag[];

  useEffect(()=>{
    // 判断应该显示哪个tag
    let targetId = 0;
    if(defaultTags.length > 0) {
      // 有系统的情况下
      targetId = defaultTags[0].id;
    }
    else {
      // 无系统的情况下
      // @ts-ignore
      let tags = [].concat(props.tags).filter(Boolean);
      if(tags && tags.length) {
        // @ts-ignore
        targetId = tags[0].id;
      }
    }

    if(Number(targetId) != Number(props.id)) {
      props.onUpdateTagId(Number(targetId));
    }
  }, [defaultTags.length]);

  const [showMore, setShowMore] = useState(false);
  const [shouldRender, setShouldRender] = useState(true);
  const arrowClass = classNames('a-r', { more: showMore });
  const renderDefaultTags = () =>
    defaultTags.map((tag: IOneTag) => {
      return (
        <OneTag
          name={tag.name}
          id={tag.id}
          key={tag.id}
          isActive={tag.id === props.id}
          clickTag={id => {
            props.clickTag(id);
            setShowMore(false);
          }}
          sp
        />
      );
    });
  const renderNormalTags = () => {
    // @ts-ignore
    let tags = [].concat(props.tags).filter(Boolean);
    // tags.sort((a: any, b: any) => {
    //   if (b.id == props.id) {
    //     return 1;
    //   }
    //   return 0;
    // });

    return tags.map((tag: any) => {
      return (
        <OneTag
          name={tag.name}
          id={tag.id}
          key={tag.name + tag.id}
          isActive={tag.id === props.id}
          clickTag={id => {
            props.clickTag(id);
            setShowMore(false);
          }}
        />
      );
    });
  };
  const renderShowMoreTags = () => {
    const tags = [...defaultTags, ...props.tags];
    return tags.map(tag => (
      <OneShowMoreTag
        key={tag.id}
        clickTag={id => {
          props.clickTag(id);
          setShowMore(false);
        }}
        isActive={tag.id === props.id}
        name={tag.name}
        id={tag.id}
      />
    ));
  };
  const { componentColor, backgroundColor, mainColor, h1Color } = useContext(
    ThemeContext
  );

  const renderChildren = (dom: any, container: any) => {
    return dom;
  };

  useEffect(() => {
    const eventListener = (event: any) => {
      if (event && Number(event.data) == 0) {
        setShouldRender(true);
      } else {
        setShouldRender(false);
      }
    };
    window.addEventListener('SWIPER_INDEX_CHANGE', eventListener);

    return () => {
      window.removeEventListener('SWIPER_INDEX_CHANGE', eventListener);
    };
  }, []);

  const typeListRef = createRef<HTMLDivElement>();
  // type list 阻止垂直方向滚动
  useEffect(() => {
    const sp = function(e: TouchEvent) {
      //e.preventDefault();
      e.stopPropagation();
    };
    try {
      if (typeListRef.current) {
        typeListRef.current.addEventListener('touchstart', sp, {
          passive: false
        });
      }
    } catch (e) {}
    return () => {
      try {
        if (typeListRef.current) {
          typeListRef.current.removeEventListener('touchstart', sp);
        }
      } catch (e) {}
    };
  }, []);

  useEffect(() => {
    try {
      if (
        typeListRef &&
        typeListRef.current &&
        typeof typeListRef.current.scrollTo == 'function'
      ) {
        typeListRef.current.scrollTo(lastTagTypeScrollLeft, 0);
      }
    } catch (e) {}
  }, [!!props.isFixed && shouldRender, typeListRef]);

  const renderMethod =
    !!props.isFixed && shouldRender ? createPortal : renderChildren;

  const pageContext = useContext(PageContext);
  const portal = usePortalDom({
    targetClassName:'fixed-top-wrapper',
    parentElement:window.document.getElementById(`page-top-${pageContext.cid}`)! as HTMLDivElement
  });

  return <>
    {props.isFixed ? <div ref={ref} style={{height:40}} /> : null}
    {renderMethod(
      <div ref={props.isFixed ? null : ref}
        className={classNames('type-list-wrapper', {fixed:props.isFixed})}>
        <div
          className={classNames('type-list-tag-row')}
          style={{
            backgroundColor: props.isFixed
              ? chroma(componentColor)
                  .alpha(1)
                  .css()
              : componentColor
          }}
        >
          <div
            className="type-list"
            ref={typeListRef}
            onScroll={(e: any) => {
              if (e && e.currentTarget) {
                lastTagTypeScrollLeft = e.currentTarget.scrollLeft;
              }
            }}
          >
            {showMore ? (
              <span className={'one-type sp active'} style={{ color: h1Color }}>
                {intl.get('Select')}
              </span>
            ) : (
              <>
                {renderDefaultTags()}
                {renderNormalTags()}
              </>
            )}
          </div>
          <div
            className={classNames('type-list-more-wrapper')}
            onClick={() => setShowMore(!showMore)}
          >
            <img
              src={require('../../assets/arrow-right.png')}
              alt=""
              className={arrowClass}
            />
          </div>
        </div>
        {showMore && (
          <div
            className={classNames('type-list-wrapper-more')}
            style={{
              backgroundColor: props.isFixed
                ? chroma(componentColor)
                    .alpha(1)
                    .css()
                : componentColor,
              borderTopColor: chroma(h1Color)
                .alpha(0.1)
                .css()
            }}
          >
            {renderShowMoreTags()}
          </div>
        )}
      </div>,
      portal!
    )}
  </>;
});

export default Tags;
